/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { State } from './editor/objects'

// Action Types
export const SET_AUDIT_DATA = 'SET_AUDIT_DATA'
export const CLEAR_AUDIT_DATA = 'CLEAR_AUDIT_DATA'

// Interfaces
export interface LottieComponentData {
  id: string
  name: string
  type: string
  componentName: string
  fileSize: number
}

export interface ComponentWithLibrary {
  id: string
  name: string
  libraryName: string
  componentName: string
}

export interface LegacyComponent extends ComponentWithLibrary {
  warningBuildNotes: string
}

export interface ComponentScreen<T> {
  screenId: string
  screenName: string
  components: T[]
}

// Use ComponentScreen for all screen types
export type LottieScreenData = ComponentScreen<LottieComponentData>
export type LegacyScreenData = ComponentScreen<LegacyComponent>
export type PrivateScreenData = ComponentScreen<ComponentWithLibrary>
export type RNVersionMismatchScreen = ComponentScreen<ComponentWithLibrary>

export interface AuditData {
  nestedLists: Array<{
    id: string
    name: string
  }>
  componentCounts: Array<{
    screenIndex: number
    id: number | string
    name: string
    component_count: number
  }>
  cacheHitRatios: CacheHitRatios
  orphanedScreens: Array<
    Array<{
      id: string
      name: string
    }>
  >
  liveRowCounts: Array<{
    table_name: string
    estimated_live_rows: number
  }>
  lottieComponents: LottieScreenData[]
  legacyComponents: LegacyScreenData[]
  privateComponents: PrivateScreenData[]
  rnVersionMismatch: RNVersionMismatchScreen[]
}

export interface CacheHitRatios {
  table_cache_hit_ratio: CacheHitRatio
  index_cache_hit_ratio: CacheHitRatio
  per_table_cache_hits: PerTableCacheHit[]
}

export interface CacheHitRatio {
  heap_read?: string
  heap_hit?: string
  ratio: string
  index_read?: string
  index_hit?: string
}

export interface PerTableCacheHit {
  table_name: string
  heap_blks_read: number
  heap_blks_hit: number
  heap_hit_ratio_pct: string
  idx_blks_read: string
  idx_blks_hit: string
  index_hit_ratio_pct: string
}

export interface AuditState {
  [appId: string]: AuditData | null
}

interface SetAuditDataAction {
  type: typeof SET_AUDIT_DATA
  payload: {
    appId: string
    data: AuditData
  }
}

interface ClearAuditDataAction {
  type: typeof CLEAR_AUDIT_DATA
  payload: {
    appId: string
  }
}

type AuditAction = SetAuditDataAction | ClearAuditDataAction

// Action Creators
export const setAuditData = (
  appId: string,
  data: AuditData
): SetAuditDataAction => ({
  type: SET_AUDIT_DATA,
  payload: { appId, data },
})

export const clearAuditData = (appId: string): ClearAuditDataAction => ({
  type: CLEAR_AUDIT_DATA,
  payload: { appId },
})

// Selectors
export const getAuditData = (state: State, appId: string): AuditData | null => {
  if (!state?.audit) {
    return null
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return state.audit[appId] || null
}

// Reducer
const initialState: AuditState = {}

export default function reducer(
  state = initialState,
  action: AuditAction = {} as AuditAction
): AuditState {
  switch (action.type) {
    case SET_AUDIT_DATA:
      return {
        ...state,
        [action.payload.appId]: action.payload.data,
      }
    case CLEAR_AUDIT_DATA: {
      const newState = { ...state }
      delete newState[action.payload.appId]

      return newState
    }
    default:
      return state
  }
}
